import { useState } from 'react';
import { Time24Input } from '../../components/ui/time24-input';
import { useTranslation, useI18nStore } from '../../lib/i18n';
const tt = (k, p) => useI18nStore.getState().t(`startStopAdmin.${k}`, p);
import { PlayCircle, Plus, X, Gauge, Paperclip, Clock, MapPin, Lock } from 'lucide-react';

const TYPES = ['text', 'number', 'time', 'date', 'photo', 'boolean', 'select'];
const TYPE_LABELS = new Proxy({}, { get: (_, k) => (TYPES.includes(k) ? tt(`ty_${k}`) : undefined), ownKeys: () => TYPES, getOwnPropertyDescriptor: () => ({ enumerable: true, configurable: true }) });
const KM_TPL = {
  start: { key: 'start_km', label: 'Başlangıç KM', type: 'number', required: true, options: [], help: 'Yazın veya göstergenin fotoğrafını çekin', default_now: false, validation: { integer: true, min: 0 } },
  stop: { key: 'end_km', label: 'Bitiş KM', type: 'number', required: true, options: [], help: "Başlangıç KM'den büyük olmalı", default_now: false, validation: { integer: true, greater_than: 'start_km' } },
};
export const isKm = (f) => f.type === 'number' && /km/i.test(`${f.key} ${f.label}`);
const KM_HINT = { start: /ba[sş]l|start|ilk/i, stop: /biti|end|stop|son/i };
const findKm = (fields, phase) => {
  const other = phase === 'start' ? 'stop' : 'start';
  const cand = fields.map((f, i) => [f, i]).filter(([f]) => isKm(f) && f.ss_phase !== other);
  const hit = cand.find(([f]) => KM_HINT[phase].test(`${f.key} ${f.label}`)) || cand.find(([f]) => !KM_HINT[other].test(`${f.key} ${f.label}`));
  return hit ? hit[1] : null;
};
const PHASES = [['', 'tInForm'], ['start', null], ['stop', null]];
const TONE = {
  start: { head: 'text-emerald-700 bg-emerald-50 dark:bg-emerald-950/30', btn: 'bg-emerald-600 text-white border-emerald-600' },
  stop: { head: 'text-rose-700 bg-rose-50 dark:bg-rose-950/30', btn: 'bg-rose-600 text-white border-rose-600' },
  '': { btn: 'bg-slate-700 text-white border-slate-700' },
};

const Opt = ({ checked, onChange, title, desc, testId }) => (
  <label className="flex items-start gap-2 cursor-pointer">
    <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} className="mt-0.5 w-4 h-4 accent-sky-600" data-testid={testId} />
    <span>
      <span className="block text-xs font-semibold text-slate-800 dark:text-slate-100">{title}</span>
      <span className="block text-[11px] text-slate-500">{desc}</span>
    </span>
  </label>
);

function AddInline({ phase, onAdd }) {
  const [label, setLabel] = useState('');
  const [type, setType] = useState('number');
  const [open, setOpen] = useState(false);
  const submit = () => { if (label.trim()) { onAdd(phase, label.trim(), type); setLabel(''); setOpen(false); } };
  if (!open) {
    return <button type="button" onClick={() => setOpen(true)} className="w-full text-[11px] text-sky-700 hover:bg-sky-50 dark:hover:bg-sky-950/30 py-1.5 flex items-center justify-center gap-1" data-testid={`tpl-ss-add-${phase}`}><Plus className="h-3 w-3" />{tt('tNewField')}</button>;
  }
  return (
    <div className="flex items-center gap-1 p-1.5">
      <input autoFocus value={label} onChange={(e) => setLabel(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), submit())}
        placeholder={tt('tFieldNamePh')} className="flex-1 min-w-0 h-7 text-xs rounded border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-2" data-testid={`tpl-ss-add-${phase}-label`} />
      <select value={type} onChange={(e) => setType(e.target.value)} className="h-7 text-[11px] rounded border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-1" data-testid={`tpl-ss-add-${phase}-type`}>
        {Object.entries(TYPE_LABELS).filter(([v]) => v !== 'select' && v !== 'time').map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </select>
      <button type="button" onClick={submit} className="h-7 px-2 rounded bg-sky-600 text-white text-[11px] font-semibold" data-testid={`tpl-ss-add-${phase}-save`}>{tt('tAdd')}</button>
      <button type="button" onClick={() => setOpen(false)} className="p-1 text-slate-400"><X className="h-3.5 w-3.5" /></button>
    </div>
  );
}

function PhaseColumn({ phase, title, fields, patch, setFields, onAdd, attachments, onAttachments }) {
  const list = fields.map((f, i) => [f, i]).filter(([f]) => f.ss_phase === phase);
  const unassigned = fields.map((f, i) => [f, i]).filter(([f]) => !f.ss_phase && f.label && f.type !== 'time');
  const km = KM_TPL[phase];
  const kmIdx = list.find(([f]) => isKm(f))?.[1];
  const addKm = () => setFields((prev) => {
    const i = findKm(prev, phase);
    if (i == null) return [...prev, { ...km, label: tt(phase === 'start' ? 'startKm' : 'endKm'), help: '', key: prev.some((f) => f.key === km.key) ? `${km.key}_2` : km.key, ss_phase: phase }];
    return prev.map((f, j) => (j === i ? { ...f, ss_phase: phase, required: true } : f));
  });
  return (
    <div className="rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 overflow-hidden" data-testid={`tpl-ss-col-${phase}`}>
      <div className={`px-2 py-1.5 text-[11px] font-bold uppercase tracking-wide ${TONE[phase].head}`}>{title} <span className="font-normal normal-case">· {tt(list.length === 1 ? 'tNFields1' : 'tNFields', { n: list.length })}</span></div>
      <div className="px-2 py-1 text-[11px] text-slate-500 border-b border-slate-100 dark:border-slate-800">{tt('tAutoLoc')}</div>
      {list.length === 0 && <div className="px-2 py-2 text-[11px] text-slate-400 italic">{tt('tNoFields')}</div>}
      {list.map(([f, i]) => (
        <div key={i} className="flex items-center gap-2 px-2 py-1.5 border-b border-slate-100 dark:border-slate-800 text-xs" data-testid={`tpl-ss-${phase}-item-${f.key || i}`}>
          {i === kmIdx && <span className="inline-flex items-center gap-0.5 rounded bg-sky-100 text-sky-700 px-1 text-[10px] font-semibold shrink-0" title={tt('tKmPhotoTitle')} data-testid={`tpl-ss-${phase}-km-badge`}><Gauge className="h-3 w-3" />{tt('tKmPhoto')}</span>}
          <span className="flex-1 min-w-0 truncate font-medium">{f.label || <i className="text-slate-400">{tt('tUnnamed')}</i>}</span>
          <span className="text-[10px] text-slate-400">{TYPE_LABELS[f.type]}</span>
          <label className="flex items-center gap-1 text-[11px]"><input type="checkbox" checked={!!f.required} onChange={(e) => patch(i, { required: e.target.checked })} data-testid={`tpl-ss-${phase}-required-${f.key || i}`} />{tt('tRequired')}</label>
          <button type="button" onClick={() => patch(i, { ss_phase: null })} className="p-0.5 text-slate-400 hover:text-rose-600" title={tt('tRemoveFromPhase')} data-testid={`tpl-ss-${phase}-remove-${f.key || i}`}><X className="h-3.5 w-3.5" /></button>
        </div>
      ))}
      {phase === 'stop' && attachments && (
        <div className="flex items-center gap-2 px-2 py-1.5 border-b border-slate-100 dark:border-slate-800 text-xs" data-testid="tpl-ss-attachments-row">
          <Paperclip className="h-3.5 w-3.5 text-slate-500 shrink-0" />
          <span className={`flex-1 font-medium ${attachments.show ? '' : 'text-slate-400 line-through'}`}>{tt('tAttachTitle')} <span className="text-[10px] font-normal text-slate-400">{tt('tAttachMulti')}</span></span>
          <label className="flex items-center gap-1 text-[11px]"><input type="checkbox" checked={!!attachments.show} onChange={(e) => onAttachments({ show: e.target.checked, required: e.target.checked && attachments.required })} data-testid="tpl-ss-attachments-show" />{tt('tShow')}</label>
          <label className={`flex items-center gap-1 text-[11px] ${attachments.show ? '' : 'opacity-40'}`}><input type="checkbox" checked={!!attachments.required} disabled={!attachments.show} onChange={(e) => onAttachments({ ...attachments, required: e.target.checked })} data-testid="tpl-ss-attachments-required" />{tt('tRequired')}</label>
        </div>
      )}
      {kmIdx == null && (
        <button type="button" onClick={addKm} className="w-full text-[11px] text-sky-700 hover:bg-sky-50 dark:hover:bg-sky-950/30 py-1.5 border-b border-slate-100 dark:border-slate-800 flex items-center justify-center gap-1" data-testid={`tpl-ss-add-km-${phase}`}>
          <Gauge className="h-3 w-3" />{tt('tAddKm', { label: tt(phase === 'start' ? 'startKm' : 'endKm') })}
        </button>
      )}
      {unassigned.length > 0 && (
        <select value="" onChange={(e) => e.target.value !== '' && patch(Number(e.target.value), { ss_phase: phase })}
          className="w-full h-8 text-[11px] border-0 border-b border-slate-100 dark:border-slate-800 bg-transparent px-2 text-slate-600" data-testid={`tpl-ss-pick-${phase}`}>
          <option value="">{tt('tPickExisting')}</option>
          {unassigned.map(([f, i]) => <option key={i} value={i}>{f.label}</option>)}
        </select>
      )}
      <AddInline phase={phase} onAdd={onAdd} />
    </div>
  );
}

function AssignTable({ fields, patch }) {
  if (!fields.length) return null;
  return (
    <div className="rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900" data-testid="tpl-ss-assign-table">
      <div className="px-2 py-1.5 text-[11px] font-bold uppercase tracking-wide text-slate-600 border-b border-slate-100 dark:border-slate-800">{tt('tAllFields')}</div>
      {fields.map((f, i) => (
        <div key={i} className="flex items-center gap-2 px-2 py-1 border-b last:border-0 border-slate-100 dark:border-slate-800 text-xs" data-testid={`tpl-ss-row-${i}`}>
          <span className="flex-1 min-w-0 truncate">{f.label || <i className="text-slate-400">{tt('tUnnamed')}</i>}<span className="ml-1 text-[10px] text-slate-400">{TYPE_LABELS[f.type]}</span></span>
          <div className="inline-flex rounded-md border border-slate-200 dark:border-slate-700 overflow-hidden">
            {PHASES.map(([v, l]) => {
              const on = (f.ss_phase || '') === v;
              const locked = v && f.type === 'time';
              return (
                <button key={v || 'form'} type="button" disabled={locked} title={locked ? tt('tTimeLocked') : undefined} onClick={() => patch(i, { ss_phase: v || null })} data-testid={`tpl-ss-row-${i}-${v || 'form'}`}
                  className={`px-2 py-0.5 text-[11px] font-semibold border-l first:border-l-0 transition-colors ${locked ? 'opacity-30 cursor-not-allowed ' : ''}${on ? TONE[v].btn : 'border-slate-200 dark:border-slate-700 text-slate-500 hover:bg-slate-50 dark:hover:bg-slate-800'}`}>{l ? tt(l) : (v === 'start' ? 'Start' : 'Stop')}</button>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

function WindowPicker({ value, onChange }) {
  const set = (k) => (v) => onChange({ ...value, [k]: v });
  const ws = value.start_stop_window_start || '';
  const we = value.start_stop_window_end || '';
  const overnight = ws && we && we <= ws;
  const tIn = 'h-8 rounded-md border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-2 text-sm tabular-nums';
  return (
    <div className="rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-2 space-y-1.5" data-testid="tpl-ss-window">
      <div className="text-[11px] font-bold uppercase tracking-wide text-slate-600 flex items-center gap-1"><Clock className="h-3.5 w-3.5" />{tt('tWindow')}</div>
      <div className="flex flex-wrap items-center gap-2 text-xs">
        <label className="flex items-center gap-1">{tt('tWindowStart')}<Time24Input value={ws} onChange={set('start_stop_window_start')} className={`${tIn} w-20`} testId="tpl-ss-window-start" /></label>
        <span className="text-slate-400">→</span>
        <label className="flex items-center gap-1">{tt('tWindowEnd')}<Time24Input value={we} onChange={set('start_stop_window_end')} className={`${tIn} w-20`} testId="tpl-ss-window-end" /></label>
        {overnight && <span className="rounded bg-indigo-100 text-indigo-700 px-1.5 py-0.5 text-[10px] font-bold" data-testid="tpl-ss-window-overnight">{tt('tOvernight')}</span>}
        {(ws || we) && <button type="button" onClick={() => onChange({ ...value, start_stop_window_start: '', start_stop_window_end: '' })} className="text-[11px] text-slate-500 underline">{tt('tClear')}</button>}
      </div>
      <p className="text-[11px] text-slate-500">
        {ws || we
          ? tt('tWindowSet', { ws: ws || '00:00', nd: overnight ? tt('tNextDayPrefix') : '', we: we || '24:00' })
          : tt('tWindowEmpty')}
      </p>
    </div>
  );
}

const parsePoint = (txt) => {
  const m = String(txt || '').trim().match(/^(-?\d+(?:\.\d+)?)\s*[,;\s]\s*(-?\d+(?:\.\d+)?)$/);
  if (!m) return null;
  const lat = Number(m[1]); const lng = Number(m[2]);
  return Math.abs(lat) <= 90 && Math.abs(lng) <= 180 ? { lat, lng } : null;
};

function StartPointInput({ value, onChange, depotLocation }) {
  const [txt, setTxt] = useState(value ? `${value.lat},${value.lng}` : '');
  const bad = txt.trim() && !parsePoint(txt);
  const inCls = 'h-8 w-full rounded-md border bg-white dark:bg-slate-900 px-2 text-xs tabular-nums';
  const onTxt = (v) => {
    setTxt(v);
    const pt = parsePoint(v);
    if (pt) onChange({ ...pt, label: value?.label || '' });
    else if (!v.trim()) onChange(null);
  };
  return (
    <div className="space-y-1.5 rounded-md border border-slate-200 dark:border-slate-700 bg-white/70 dark:bg-slate-900/50 p-2" data-testid="tpl-ss-preset-point">
      <label className="block text-[11px] font-semibold text-slate-600">{tt('pCoords')}
        <input value={txt} onChange={(e) => onTxt(e.target.value)} placeholder="51.17452792723055,5.828706854830536"
          className={`${inCls} mt-0.5 ${bad ? 'border-rose-400' : 'border-slate-300 dark:border-slate-700'}`} data-testid="tpl-ss-preset-point-coords" />
      </label>
      {bad && <p className="text-[10px] font-semibold text-rose-600" data-testid="tpl-ss-preset-point-invalid">{tt('pCoordsInvalid')}</p>}
      <label className="block text-[11px] font-semibold text-slate-600">{tt('pPointLabel')}
        <input value={value?.label || ''} disabled={!value} onChange={(e) => onChange({ ...value, label: e.target.value })} placeholder={tt('pPointLabelPh')}
          className={`${inCls} mt-0.5 border-slate-300 dark:border-slate-700 disabled:opacity-50`} data-testid="tpl-ss-preset-point-label" />
      </label>
      <div className="flex items-center gap-3 text-[10px]">
        {value && <a href={`https://www.google.com/maps?q=${value.lat},${value.lng}`} target="_blank" rel="noreferrer" className="text-sky-600 underline" data-testid="tpl-ss-preset-point-map">{tt('pOpenMap')}</a>}
        {!value && <span className="text-slate-500">{depotLocation ? tt('pUsesDepot', { c: `${depotLocation.lat.toFixed(5)}, ${depotLocation.lng.toFixed(5)}` }) : ''}</span>}
        {!value && depotLocation && <button type="button" onClick={() => onTxt(`${depotLocation.lat},${depotLocation.lng}`)} className="text-slate-600 underline" data-testid="tpl-ss-preset-point-use-depot">{tt('pFillDepot')}</button>}
      </div>
    </div>
  );
}

function PresetPicker({ value, onChange, routes, depotLocation, isCompany }) {
  const p = { enabled: false, time: '', route_times: {}, use_depot_location: false, ...(value.start_stop_preset || {}) };
  const set = (patch) => onChange({ ...value, start_stop_preset: { ...p, ...patch } });
  const setRoute = (id, v) => set({ route_times: { ...p.route_times, [id]: v } });
  const tIn = 'h-8 rounded-md border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-2 text-sm tabular-nums w-20';
  return (
    <div className="rounded-md border border-amber-200 dark:border-amber-800 bg-amber-50/50 dark:bg-amber-950/20 p-2 space-y-2" data-testid="tpl-ss-preset">
      <Opt checked={p.enabled} onChange={(v) => set({ enabled: v })} testId="tpl-ss-preset-enabled"
        title={<span className="flex items-center gap-1"><Lock className="h-3.5 w-3.5 text-amber-600" />{tt('pTitle')}</span>} desc={tt('pDesc')} />
      {p.enabled && (
        <div className="pl-6 space-y-2">
          <label className="flex items-center gap-2 text-xs">{tt(isCompany ? 'pDefaultTimeCompany' : 'pDefaultTime')}
            <Time24Input value={p.time || ''} onChange={(v) => set({ time: v })} className={tIn} testId="tpl-ss-preset-time" />
            {p.time && <button type="button" onClick={() => set({ time: '' })} className="text-[11px] text-slate-500 underline" data-testid="tpl-ss-preset-time-clear">{tt('tClear')}</button>}
          </label>
          {routes?.length > 0 && (
            <div className="rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 max-h-56 overflow-y-auto" data-testid="tpl-ss-preset-routes">
              <div className="px-2 py-1 text-[11px] font-bold uppercase tracking-wide text-slate-600 border-b border-slate-100 dark:border-slate-800">{tt('pRouteTimes')}</div>
              {routes.map((r) => (
                <div key={r.id} className="flex items-center gap-2 px-2 py-1 border-b last:border-0 border-slate-100 dark:border-slate-800 text-xs" data-testid={`tpl-ss-preset-route-${r.id}`}>
                  <span className="flex-1 min-w-0 truncate font-medium">{r.route_code}{r.route_name ? <span className="text-slate-400 font-normal"> · {r.route_name}</span> : null}</span>
                  <Time24Input value={p.route_times[r.id] || ''} onChange={(v) => setRoute(r.id, v)} className={tIn} testId={`tpl-ss-preset-route-time-${r.id}`} />
                  <span className="w-20 text-[10px] text-slate-400">{p.route_times[r.id] ? '' : (p.time ? tt('pUsesDefault', { time: p.time }) : tt('pFree'))}</span>
                </div>
              ))}
            </div>
          )}
          <Opt checked={p.use_depot_location} onChange={(v) => set({ use_depot_location: v })} testId="tpl-ss-preset-location"
            title={<span className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5 text-amber-600" />{tt('pLocation')}</span>} desc={tt('pLocationDesc')} />
          {p.use_depot_location && <StartPointInput value={p.start_location} onChange={(v) => set({ start_location: v })} depotLocation={depotLocation} />}
          {p.use_depot_location && !p.start_location && !isCompany && !depotLocation && <p className="text-[11px] font-semibold text-rose-700" data-testid="tpl-ss-preset-no-coords">{tt('pNoCoords')}</p>}
          <p className="text-[11px] text-amber-800 dark:text-amber-300">{tt('pHint')}</p>
        </div>
      )}
    </div>
  );
}

export function StartStopTemplateOptions({ value, onChange, fields, setFields, onAddField, routes, depotLocation, isCompany }) {
  useTranslation();
  const set = (k) => (v) => onChange({ ...value, [k]: v });
  const on = value.start_stop_enabled;
  const att = { show: true, required: false, ...(value.start_stop_fields?.attachments || {}) };
  const patch = (idx, p) => setFields((prev) => prev.map((f, i) => (i === idx ? { ...f, ...p } : f)));
  return (
    <div className={`border rounded-lg p-3 space-y-2 ${on ? 'border-sky-300 bg-sky-50/50 dark:border-sky-800 dark:bg-sky-950/20' : 'border-slate-200 dark:border-slate-700'}`} data-testid="start-stop-template-options">
      <Opt checked={on} onChange={set('start_stop_enabled')} testId="tpl-start-stop-enabled"
        title={<span className="flex items-center gap-1.5"><PlayCircle className="h-3.5 w-3.5 text-sky-600" />{tt('tSsMode')}</span>}
        desc={tt('tSsModeDesc')} />
      {on && (
        <div className="pl-6 space-y-2">
          <Opt checked={value.start_stop_required} onChange={set('start_stop_required')} testId="tpl-start-stop-required"
            title={tt('tRequired')} desc={tt('tRequiredDesc')} />
          <Opt checked={value.start_stop_hide_form} onChange={set('start_stop_hide_form')} testId="tpl-start-stop-hide-form"
            title={tt('tHideForm')} desc={tt('tHideFormDesc')} />
          <Opt checked={value.start_stop_hide_route_report} onChange={set('start_stop_hide_route_report')} testId="tpl-start-stop-hide-route-report"
            title={tt('tHideRouteReport')} desc={tt('tHideRouteReportDesc')} />
          <WindowPicker value={value} onChange={onChange} />
          <PresetPicker value={value} onChange={onChange} routes={routes} depotLocation={depotLocation} isCompany={isCompany} />
          <div className="grid sm:grid-cols-2 gap-2">
            <PhaseColumn phase="start" title={tt('tAskStart')} fields={fields} patch={patch} setFields={setFields} onAdd={onAddField} />
            <PhaseColumn phase="stop" title={tt('tAskStop')} fields={fields} patch={patch} setFields={setFields} onAdd={onAddField}
              attachments={att} onAttachments={(v) => onChange({ ...value, start_stop_fields: { ...(value.start_stop_fields || {}), attachments: v } })} />
          </div>
          <AssignTable fields={fields} patch={patch} />
          <p className="text-[11px] text-sky-800 dark:text-sky-300 bg-sky-100/70 dark:bg-sky-900/30 rounded-md px-2 py-1.5" data-testid="tpl-ss-phase-hint">
            {tt('tHint')}
          </p>
        </div>
      )}
    </div>
  );
}
